import React, { useState } from 'react';
import { useProcessing } from '../../context/ProcessingContext';
import { useUpload } from '../../hooks/useUpload';
import { parseAnalysisError } from '../../lib/analysisErrors';
import { Loader2, CheckCircle2, AlertCircle, X, FileText, Brain, Search, Database, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import type { AnalysisStatus } from '../../types';

interface UploadState {
    id: string;
    filename: string;
    status: AnalysisStatus;
    stepIndex: number;
    error?: string;
}

// Au-delà de ce nombre de fichiers simultanés, on bascule en vue compacte agrégée
// (l'import en masse empilait des dizaines de grosses cartes — illisible)
const COMPACT_THRESHOLD = 5;

// ─── Unified 5-step thresholds (aligned with MissionProgressWidget) ─────────
const STEP_THRESHOLDS = [
    { label: 'Réception', pct: 10, Icon: FileText },
    { label: 'Extraction', pct: 30, Icon: Search },
    { label: 'Analyse IA', pct: 60, Icon: Brain },
    { label: 'Vectorisation', pct: 85, Icon: Database },
    { label: 'Terminé', pct: 100, Icon: CheckCircle2 },
];

const getProgressPercentage = (status: AnalysisStatus): number => {
    switch (status) {
        case 'received': return 10;
        case 'analyzing_doc': return 35;
        case 'analyzing_agents': return 65;
        case 'completed': return 100;
        default: return 0;
    }
};

const getStatusText = (status: AnalysisStatus, error?: string): string => {
    switch (status) {
        case 'received': return "Réception du document…";
        case 'analyzing_doc': return "Extraction et lecture du contenu…";
        case 'analyzing_agents': return "Analyse approfondie par l'IA…";
        case 'completed': return "Analyse terminée avec succès.";
        case 'error': return parseAnalysisError(error).title;
        default: return "En attente…";
    }
};

// Le catalogue de `lib/analysisErrors` remplace le reniflage local : le serveur
// écrit désormais un code stable (`AI_TRUNCATED: …`) et c'est lui qui fait foi.
const shortError = (error?: string): string => parseAnalysisError(error).title;

const AnalysisProgressWidget: React.FC = () => {
    const { activeUploads, dismissUpload, getRetryFile } = useProcessing();
    const { uploadCandidate } = useUpload();
    const [collapsed, setCollapsed] = useState(false);
    const [retrying, setRetrying] = useState<Set<string>>(new Set());

    // Relance d'un CV en échec : on renvoie le fichier d'origine. Le serveur
    // déduplique sur son empreinte, retombe donc sur la MÊME fiche, et repart en
    // mode condensé s'il s'était coupé la fois précédente.
    const retry = async (id: string) => {
        const file = getRetryFile(id);
        if (!file || retrying.has(id)) return;
        setRetrying(prev => new Set(prev).add(id));
        const res = await uploadCandidate(file);
        setRetrying(prev => { const next = new Set(prev); next.delete(id); return next; });
        // Une fiche identifiée reprend la même carte (startMonitoring écrase l'entrée).
        // Un id temporaire, lui, laisserait deux cartes : on retire l'ancienne.
        if (res?.success && res.candidateId !== id) dismissUpload(id);
    };

    if (activeUploads.size === 0) return null;

    const uploads: UploadState[] = Array.from(activeUploads.values());
    const done    = uploads.filter(u => u.status === 'completed');
    const errors  = uploads.filter(u => u.status === 'error');
    const running = uploads.filter(u => u.status !== 'completed' && u.status !== 'error');

    // ─── Vue compacte agrégée (import en masse) ──────────────────────────────
    if (uploads.length > COMPACT_THRESHOLD) {
        const globalPct = Math.round(uploads.reduce((s, u) => s + getProgressPercentage(u.status), 0) / uploads.length);
        return (
            <div className="mb-2 rounded-xl border border-slate-200 bg-white shadow-sm animate-in fade-in">
                {/* Bandeau de synthèse */}
                <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0
                            ${running.length > 0 ? 'bg-indigo-50 text-indigo-600' : errors.length > 0 ? 'bg-amber-50 text-amber-600' : 'bg-emerald-100 text-emerald-600'}`}>
                            {running.length > 0 ? <Loader2 size={18} className="animate-spin" /> : errors.length > 0 ? <AlertCircle size={18} /> : <CheckCircle2 size={18} />}
                        </div>
                        <div className="min-w-0">
                            <h4 className="font-semibold text-slate-800 text-sm">
                                Import de {uploads.length} CV — {done.length} terminé{done.length > 1 ? 's' : ''}
                                {running.length > 0 && `, ${running.length} en cours`}
                                {errors.length > 0 && <span className="text-red-600">, {errors.length} en erreur</span>}
                            </h4>
                            <div className="h-1.5 w-56 bg-slate-100 rounded-full overflow-hidden mt-1.5">
                                <div className={`h-full rounded-full transition-all duration-700 ${running.length === 0 && errors.length === 0 ? 'bg-emerald-500' : 'bg-indigo-500'}`}
                                     style={{ width: `${globalPct}%` }} />
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {done.length > 0 && running.length === 0 && (
                            <button onClick={() => window.location.reload()}
                                className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 rounded-md transition-colors">
                                <RefreshCw size={12} /> Actualiser
                            </button>
                        )}
                        {done.length > 0 && (
                            <button onClick={() => done.forEach(u => dismissUpload(u.id))}
                                className="text-xs font-medium px-3 py-1.5 text-slate-500 hover:bg-slate-100 rounded-md transition-colors">
                                Masquer les terminés
                            </button>
                        )}
                        <button onClick={() => setCollapsed(c => !c)} title={collapsed ? 'Déplier' : 'Replier'}
                            className="text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-100 rounded-full transition-colors">
                            {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                        </button>
                    </div>
                </div>

                {/* Liste compacte scrollable — erreurs d'abord, puis en cours, puis terminés */}
                {!collapsed && (
                    <div className="border-t border-slate-100 max-h-64 overflow-y-auto divide-y divide-slate-50">
                        {[...errors, ...running, ...done].map((upload) => {
                            const isDone = upload.status === 'completed';
                            const isError = upload.status === 'error';
                            return (
                                <div key={upload.id} className="flex items-center gap-2.5 px-4 py-1.5 text-sm">
                                    <span className="flex-shrink-0">
                                        {isError ? <AlertCircle size={14} className="text-red-500" /> :
                                         isDone ? <CheckCircle2 size={14} className="text-emerald-500" /> :
                                         <Loader2 size={14} className="text-indigo-500 animate-spin" />}
                                    </span>
                                    <span className="truncate text-slate-700 flex-1 min-w-0 text-xs" title={upload.filename}>{upload.filename}</span>
                                    <span className={`text-[11px] flex-shrink-0 ${isError ? 'text-red-500' : isDone ? 'text-emerald-600' : 'text-slate-400'}`}
                                          title={isError ? upload.error : undefined}>
                                        {isError ? shortError(upload.error) : isDone ? 'Terminé' : getStatusText(upload.status)}
                                    </span>
                                    {isError && parseAnalysisError(upload.error).retryable && getRetryFile(upload.id) && (
                                        <button onClick={() => retry(upload.id)} disabled={retrying.has(upload.id)}
                                            className="flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 bg-white border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 rounded transition-colors flex-shrink-0">
                                            <RefreshCw size={10} className={retrying.has(upload.id) ? 'animate-spin' : ''} />
                                            {retrying.has(upload.id) ? 'Envoi…' : 'Relancer'}
                                        </button>
                                    )}
                                    <button onClick={() => dismissUpload(upload.id)} title="Fermer"
                                        className="text-slate-300 hover:text-slate-500 p-0.5 rounded transition-colors flex-shrink-0">
                                        <X size={13} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    // ─── Vue détaillée (peu de fichiers) ─────────────────────────────────────
    return (
        <div className="mb-2 space-y-3">
            {uploads.map((upload) => {
                const pct = getProgressPercentage(upload.status);
                const isDone = upload.status === 'completed';
                const isError = upload.status === 'error';

                return (
                    <div
                        key={upload.id}
                        className={`rounded-xl p-4 border shadow-sm transition-all duration-300 animate-in fade-in slide-in-from-top-2
                            ${isError ? 'border-red-200 bg-red-50/30' :
                            isDone ? 'border-emerald-200 bg-emerald-50/30' :
                            'border-slate-200 bg-white'}
                        `}
                    >
                        {/* Header row */}
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                                {/* Icon */}
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0
                                    ${isError ? 'bg-red-100 text-red-600' :
                                    isDone ? 'bg-emerald-100 text-emerald-600' :
                                    'bg-indigo-50 text-indigo-600'}
                                `}>
                                    {isError ? <AlertCircle size={20} /> :
                                     isDone ? <CheckCircle2 size={20} /> :
                                     <Loader2 size={20} className="animate-spin" />}
                                </div>

                                {/* Text */}
                                <div className="min-w-0">
                                    <h4 className="font-semibold text-slate-800 text-sm truncate max-w-xs">{upload.filename}</h4>
                                    <p className={`text-xs ${
                                        isError ? 'text-red-600 font-medium' :
                                        isDone ? 'text-emerald-600 font-medium' :
                                        'text-slate-500'
                                    }`}
                                       title={isError ? upload.error : undefined}>
                                        {getStatusText(upload.status, upload.error)}
                                    </p>
                                    {/* La conduite à tenir, sous le constat : le testeur doit savoir
                                        quoi faire sans lire une exception. Le détail technique reste
                                        au survol et dans la console. */}
                                    {isError && (
                                        <p className="text-xs text-slate-500 mt-0.5 max-w-md">
                                            {parseAnalysisError(upload.error).description}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Right-side actions */}
                            <div className="flex items-center gap-2 flex-shrink-0">
                                {/* Actualiser button — safety net for polling edge cases */}
                                {isDone && (
                                    <button
                                        onClick={() => window.location.reload()}
                                        className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 rounded-md transition-colors"
                                    >
                                        <RefreshCw size={12} />
                                        Actualiser
                                    </button>
                                )}

                                {isError && parseAnalysisError(upload.error).retryable && (
                                    getRetryFile(upload.id) ? (
                                        <button
                                            onClick={() => retry(upload.id)}
                                            disabled={retrying.has(upload.id)}
                                            className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50 rounded-md transition-colors"
                                        >
                                            <RefreshCw size={12} className={retrying.has(upload.id) ? 'animate-spin' : ''} />
                                            {retrying.has(upload.id) ? 'Envoi…' : 'Relancer'}
                                        </button>
                                    ) : (
                                        // Fichier perdu (rechargement de page) : le dire, plutôt que
                                        // d'afficher un bouton qui ne pourrait rien renvoyer.
                                        <span className="text-xs text-slate-400">Redéposez le fichier</span>
                                    )
                                )}

                                {/* Always dismissable */}
                                <button
                                    onClick={() => dismissUpload(upload.id)}
                                    title="Fermer"
                                    className="text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-100 rounded-full transition-colors"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        </div>

                        {/* Progress Bar */}
                        {!isError && (
                            <div className="relative">
                                <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                                    <div
                                        className={`h-full rounded-full transition-all duration-700 ease-out
                                            ${isDone ? 'bg-emerald-500' : 'bg-indigo-500'}`}
                                        style={{ width: `${pct}%` }}
                                    />
                                </div>

                                {/* Step labels */}
                                <div className="flex justify-between mt-2 px-0.5">
                                    {STEP_THRESHOLDS.map(({ label, pct: threshold, Icon }) => (
                                        <div
                                            key={label}
                                            className={`flex flex-col items-center gap-0.5 transition-colors duration-300
                                                ${pct >= threshold ? 'text-indigo-600' : 'text-slate-300'}
                                                ${isDone ? '!text-emerald-500' : ''}
                                            `}
                                        >
                                            <Icon size={11} />
                                            <span className="text-[10px] font-medium">{label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default AnalysisProgressWidget;
